import { useState, useEffect, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import { RefreshCw, Trash2, Loader2, FileX } from "lucide-react";

const TASK_LABEL = {
  tattoo_removal: "Tattoo entfernen",
  image_edit: "Bild bearbeiten",
  image_to_video: "Bild → Video",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export const AiLabFiles = ({ BASE }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/ai-lab/jobs?limit=100");
      setJobs(data.jobs || []);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (jobId) => {
    if (!window.confirm("Diese Quell- und Zieldatei dauerhaft löschen?")) return;
    setDeleting(jobId);
    try {
      await api.delete(`/admin/ai-lab/jobs/${jobId}`);
      setJobs((j) => j.filter((x) => x.job_id !== jobId));
    } catch (e) { setErr(formatError(e)); }
    finally { setDeleting(null); }
  };

  return (
    <div data-testid="ai-lab-files">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#666] max-w-2xl">
          Gespeicherte Quell- und Zieldateien aller Labor-Läufe (privat, nur Admin).
          Werden <b>automatisch nach 7 Tagen</b> gelöscht – oder hier jederzeit manuell.
        </p>
        <button data-testid="ai-lab-files-refresh" onClick={load}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#DDD] px-2.5 py-1.5 text-xs font-semibold text-[#444] hover:border-[#999]">
          <RefreshCw size={14} /> Aktualisieren
        </button>
      </div>

      {err && <div className="rounded-lg border border-red-300 bg-red-50 p-2 mb-3 text-xs text-red-700">{err}</div>}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#888] py-10 justify-center">
          <Loader2 className="animate-spin" size={16} /> Lädt …
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex flex-col items-center gap-2 text-sm text-[#999] py-12">
          <FileX size={28} /> Noch keine gespeicherten Dateien.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {jobs.map((j) => (
            <div key={j.job_id} data-testid={`ai-lab-file-${j.job_id}`}
              className="rounded-xl border border-[#E5E5E5] bg-white p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] text-[#666]">
                  <span className="font-semibold text-[#0A0A0A]">{TASK_LABEL[j.task] || j.task}</span>
                  {j.endpoint_label && <span> · {j.endpoint_label}</span>}
                  <span> · {fmtDate(j.created_at)}</span>
                </div>
                <button data-testid={`ai-lab-file-delete-${j.job_id}`} onClick={() => remove(j.job_id)}
                  disabled={deleting === j.job_id}
                  className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">
                  {deleting === j.job_id ? <Loader2 className="animate-spin" size={12} /> : <Trash2 size={12} />} Löschen
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#999] mb-1">Quelle</div>
                  {j.source_url ? (
                    <img src={`${BASE}${j.source_url}`} alt="Quelle" loading="lazy"
                      className="w-full h-32 object-contain rounded-lg border border-[#EEE] bg-[#FAFAFA]" />
                  ) : <div className="h-32 rounded-lg border border-dashed border-[#DDD] flex items-center justify-center text-[11px] text-[#BBB]">keine</div>}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#999] mb-1">Ergebnis</div>
                  {j.output_url ? (
                    j.kind === "video" ? (
                      <video src={`${BASE}${j.output_url}`} controls
                        className="w-full h-32 object-contain rounded-lg border border-[#EEE] bg-black" />
                    ) : (
                      <img src={`${BASE}${j.output_url}`} alt="Ergebnis" loading="lazy"
                        className="w-full h-32 object-contain rounded-lg border border-[#EEE] bg-[#FAFAFA]" />
                    )
                  ) : <div className="h-32 rounded-lg border border-dashed border-[#DDD] flex items-center justify-center text-[11px] text-[#BBB]">{j.status === "FAILED" ? "fehlgeschlagen" : "—"}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
